import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { saveNote, updateNote, getNote, getClients } from '../utils/api';
import Sidebar from '../components/Sidebar';

const DAYS = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const ADLS = ['Bathing & Hygiene', 'Grooming', 'Dressing', 'Eating', 'Toileting', 'Transfers', 'Safety'];
const IADLS = ['Meal Prep', 'Housekeeping', 'Laundry', 'Shopping', 'Transport', 'Medication'];

const ATTESTATION = 'By signing below, the Client/Authorized Representative and Caregiver acknowledge that the services documented in this Progress Note were provided as recorded, reviewed together, and are true, accurate, and complete to the best of their knowledge. Both parties confirm that the care described was delivered on the documented date(s) and that any questions or concerns have been addressed at the time of signature.';

function safeId(str) { return str.replace(/[^a-zA-Z0-9]/g, '_'); }

const emptyForm = () => ({
  clientName: '', dob: '', medicaidId: '', weekBegin: '', caregiver: '',
  timeIn: {}, timeOut: {},
  adls: Object.fromEntries(ADLS.map(a => [a, Object.fromEntries(DAYS.map(d => [d, false]))])),
  iadls: Object.fromEntries(IADLS.map(a => [a, Object.fromEntries(DAYS.map(d => [d, false]))])),
  narratives: Object.fromEntries(DAYS.map(d => [d, ''])),
  sigs: { cgImg: null, clientImg: null, cgDate: '', clientDate: '' },
});

export default function NotesFormPage() {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const assignedToName = searchParams.get('assignedToName') || '';
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 900
  );

  useEffect(() => {
    const handleResize = () => setSidebarCollapsed(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const cgCanvasRef = useRef(null);
  const clientCanvasRef = useRef(null);
  const cgSignedRef = useRef(false);
  const clientSignedRef = useRef(false);
  const isEditing = !!id;
  const isLocked = isEditing && form.status === 'signed' && !user?.isAdmin;

  // Block browser back/refresh when dirty
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Safe navigate — shows modal if dirty
  const safeNavigate = useCallback((path) => {
    if (isDirty) {
      setPendingNavigation(path);
      setShowUnsavedModal(true);
    } else {
      navigate(path);
    }
  }, [isDirty, navigate]);

  // Handle modal actions
  const handleSaveAndNavigate = async () => {
    setShowUnsavedModal(false);
    await collectAndSave();
    if (pendingNavigation) navigate(pendingNavigation);
  };

  const handleDiscardAndNavigate = () => {
    setIsDirty(false);
    setShowUnsavedModal(false);
    if (pendingNavigation) navigate(pendingNavigation);
  };

  const handleCancelNavigation = () => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  };

  // Load existing note + assigned clients
  useEffect(() => {
    getClients().then(d => setAvailableClients(d.clients || [])).catch(() => {});
    if (!id) {
      setForm(f => ({ ...f, caregiver: assignedToName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() }));
      return;
    }
    getNote(id).then(data => {
      setForm({
        ...emptyForm(),
        ...data,
        adls: data.adls || emptyForm().adls,
        iadls: data.iadls || emptyForm().iadls,
        narratives: data.narratives || emptyForm().narratives,
        sigs: (() => {
          const s = data.sigs || emptyForm().sigs;
          const toInputDate = d => { const m = d && d.match(/^(\d{2})\/(\d{2})\/(\d{4})$/); return m ? `${m[3]}-${m[1]}-${m[2]}` : (d || ''); };
          // Fallback: if date was not saved (old notes), derive Friday from weekBegin (Saturday + 6)
          const weekEndFallback = (() => {
            if (!data.weekBegin) return '';
            const d = new Date(data.weekBegin + 'T12:00:00Z');
            d.setUTCDate(d.getUTCDate() + 6);
            return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}`;
          })();
          return { ...s, cgDate: toInputDate(s.cgDate) || weekEndFallback, clientDate: toInputDate(s.clientDate) || weekEndFallback };
        })(),
      });
      setLoading(false);
    }).catch(() => { showToast('Could not load note', 'error'); setLoading(false); });
  }, [id, user]);

  // Init canvas signature pads
  useEffect(() => {
    if (loading) return;
    setTimeout(() => {
      initSigPad(cgCanvasRef.current, form.sigs?.cgImg, isLocked, cgSignedRef, () => setIsDirty(true));
      initSigPad(clientCanvasRef.current, form.sigs?.clientImg, isLocked, clientSignedRef, () => setIsDirty(true));
    }, 100);
  }, [loading]);

  function initSigPad(canvas, existingDataUrl, readOnly = false, signedRef = null, onDraw = null) {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1a2b4a';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (existingDataUrl && existingDataUrl.length > 200) {
      if (signedRef) signedRef.current = true;
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = existingDataUrl;
    }

    if (readOnly) return;

    let drawing = false, lastX = 0, lastY = 0;
    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const src = e.touches ? e.touches[0] : e;
      return [(src.clientX - rect.left) * scaleX, (src.clientY - rect.top) * scaleY];
    };
    const start = (e) => { e.preventDefault(); drawing = true; [lastX, lastY] = getPos(e); if (signedRef) signedRef.current = true; if (onDraw) onDraw(); };
    const move = (e) => {
      if (!drawing) return; e.preventDefault();
      const [x, y] = getPos(e);
      ctx.beginPath(); ctx.moveTo(lastX, lastY); ctx.lineTo(x, y); ctx.stroke();
      [lastX, lastY] = [x, y];
    };
    const stop = () => { drawing = false; };
    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', stop);
    canvas.addEventListener('mouseleave', stop);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', stop);
  }

  const clearSig = (ref, signedRef = null) => {
    const canvas = ref.current;
    if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    if (signedRef) signedRef.current = false;
    setIsDirty(true);
  };

  const MAX_SIG_BYTES = 512000; // 500 KB base64 limit
  const getCanvasData = (ref) => {
    const data = ref.current?.toDataURL('image/png') || null;
    if (data && data.length > MAX_SIG_BYTES) { showToast('Signature image is too large. Please clear and re-sign.', 'error'); return null; }
    return data;
  };

  // Returns true only if the canvas has at least one non-transparent pixel (i.e. something was drawn).
  // This is more reliable than checking data URL length or signedRef, which can be true for blank canvases.
  const canvasHasContent = (ref) => {
    const canvas = ref.current;
    if (!canvas) return false;
    const data = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) return true;
    }
    return false;
  };

  const calcTotal = (day) => {
    const inV = form.timeIn[day], outV = form.timeOut[day];
    if (!inV || !outV) return '';
    const [ih, im] = inV.split(':').map(Number);
    const [oh, om] = outV.split(':').map(Number);
    const mins = (oh * 60 + om) - (ih * 60 + im);
    if (mins <= 0) return '—';
    const h = Math.floor(mins / 60), m = mins % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  };

  const set = (field, value) => { setIsDirty(true); setForm(f => ({ ...f, [field]: value })); }
  const setTime = (field, day, value) => { setIsDirty(true); setForm(f => ({ ...f, [field]: { ...f[field], [day]: value } })); }
  const setCheck = (type, activity, day, value) => { setIsDirty(true);
    setForm(f => ({ ...f, [type]: { ...f[type], [activity]: { ...f[type][activity], [day]: value } } })); }
  const setNarrative = (day, value) => { setIsDirty(true);
    setForm(f => ({ ...f, narratives: { ...f.narratives, [day]: value } })); }

  const collectAndSave = async () => {
    if (isLocked) return;
    // Validate mandatory fields
    const missing = [];
    if (!form.clientName?.trim()) missing.push('Client Name');
    if (!form.dob?.trim()) missing.push('Date of Birth');
    if (!form.medicaidId?.trim()) missing.push('Medicaid ID');
    if (!form.weekBegin?.trim()) missing.push('Week Beginning');
    if (!form.caregiver?.trim()) missing.push('Caregiver Name');

    if (missing.length > 0) {
      showToast(`Please fill in: ${missing.join(', ')}`, 'error');
      // Scroll to top so user sees the fields
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    try {
      const cgHasSig    = canvasHasContent(cgCanvasRef);
      const clientHasSig = canvasHasContent(clientCanvasRef);
      const cgImg     = cgHasSig    ? getCanvasData(cgCanvasRef)     : null;
      const clientImg = clientHasSig ? getCanvasData(clientCanvasRef) : null;
      const bothSigned = cgHasSig && clientHasSig;
      const data = {
        ...form,
        status: bothSigned ? 'signed' : (form.status || 'draft'),
        sigs: {
          ...form.sigs,
          cgImg,
          clientImg,
        },
      };
      if (isEditing) {
        await updateNote(id, data);
        setForm(f => ({ ...f, status: data.status }));
        setIsDirty(false);
        showToast('Note updated!', 'success');
      } else {
        const res = await saveNote(data);
        setIsDirty(false);
        showToast('Note saved to cloud!', 'success');
        navigate(`/notes/${res.id}`, { replace: true });
      }
    } catch (e) {
      showToast('Could not save. Please try again.', 'error');
    } finally { setSaving(false); }
  };

  const downloadPDF = async () => {
    const { jsPDF } = await import('jspdf').then(m => ({ jsPDF: m.jsPDF })).catch(() => window.jspdf);
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
    const W = 215.9, margin = 14;
    let y = margin;

    const sf = (style = 'normal', size = 10) => { doc.setFont('helvetica', style); doc.setFontSize(size); };

    // Header
    doc.setFillColor(26, 43, 74);
    doc.rect(0, 0, W, 20, 'F');
    sf('bold', 13); doc.setTextColor(255, 255, 255);
    doc.text('Home Care Associates of MI', margin, 13);
    sf('normal', 9); doc.text('Daily Progress Notes', W - margin, 13, { align: 'right' });
    doc.setTextColor(40, 40, 40); y = 28;

    // Client info
    doc.setFillColor(247, 244, 239);
    doc.roundedRect(margin, y, W - margin * 2, 22, 2, 2, 'F');
    sf('bold', 9); doc.text('Client Name:', margin + 3, y + 7);
    sf('normal', 9); doc.text(form.clientName || '', margin + 30, y + 7);
    sf('bold', 9); doc.text('DOB:', margin + 3, y + 14); sf('normal', 9); doc.text(form.dob || '', margin + 14, y + 14);
    sf('bold', 9); doc.text('Medicaid ID:', margin + 50, y + 14); sf('normal', 9); doc.text(form.medicaidId || '', margin + 76, y + 14);
    sf('bold', 9); doc.text('Week Beginning:', W - margin - 60, y + 7); sf('normal', 9); doc.text(form.weekBegin || '', W - margin - 30, y + 7);
    sf('bold', 9); doc.text('Caregiver:', W - margin - 60, y + 14); sf('normal', 9); doc.text(form.caregiver || '', W - margin - 40, y + 14);
    y += 28;

    const colW = (W - margin * 2) / 8;
    const drawHeader = (title) => {
      doc.setFillColor(26, 43, 74); doc.rect(margin, y, W - margin * 2, 7, 'F');
      sf('bold', 9); doc.setTextColor(255, 255, 255); doc.text(title, margin + 3, y + 5);
      doc.setTextColor(40, 40, 40); y += 7;
    };
    const drawDayRow = () => {
      doc.setFillColor(240, 240, 245); doc.rect(margin, y, W - margin * 2, 7, 'F');
      sf('bold', 8); doc.setTextColor(60, 60, 80);
      DAYS.forEach((d, i) => doc.text(d, margin + colW + i * colW + colW / 2, y + 5, { align: 'center' }));
      doc.setTextColor(40, 40, 40); y += 7;
    };
    const drawRow = (label, vals, isCheck = false) => {
      const rh = 8;
      doc.setFillColor(252, 252, 252); doc.rect(margin, y, colW, rh, 'F');
      doc.setFillColor(255, 255, 255);
      for (let i = 0; i < 7; i++) doc.rect(margin + colW + i * colW, y, colW, rh, 'F');
      doc.setDrawColor(200, 200, 200); doc.rect(margin, y, W - margin * 2, rh);
      sf('normal', 8); doc.text(label, margin + 2, y + 5.5);
      vals.forEach((v, i) => {
        const cx = margin + colW + i * colW + colW / 2;
        if (isCheck) {
          // Draw checkbox border
          doc.setDrawColor(150, 150, 150);
          doc.setLineWidth(0.3);
          doc.rect(cx - 2.5, y + 1.8, 5, 4.5);
          if (v) {
            // Draw checkmark using lines (no font dependency)
            doc.setDrawColor(42, 127, 127);
            doc.setLineWidth(0.8);
            doc.line(cx - 1.8, y + 4.2, cx - 0.5, y + 5.5);
            doc.line(cx - 0.5, y + 5.5, cx + 2.0, y + 2.5);
            doc.setLineWidth(0.3);
            doc.setDrawColor(200, 200, 200);
          }
        } else {
          doc.text(v || '', cx, y + 5.5, { align: 'center' });
        }
      });
      y += rh;
    };

    // Time
    drawHeader('Time Tracking'); drawDayRow();
    drawRow('Time In', DAYS.map(d => form.timeIn[d] || ''));
    drawRow('Time Out', DAYS.map(d => form.timeOut[d] || ''));
    drawRow('Total Hours', DAYS.map(d => calcTotal(d)));
    y += 4;

    // ADLs
    drawHeader('Activities of Daily Living (ADLs)'); drawDayRow();
    ADLS.forEach(adl => drawRow(adl, DAYS.map(d => (form.adls[adl] || {})[d] || false), true));
    y += 4;

    // IADLs
    drawHeader('Instrumental Activities of Daily Living (IADLs)'); drawDayRow();
    IADLS.forEach(iadl => drawRow(iadl, DAYS.map(d => (form.iadls[iadl] || {})[d] || false), true));

    // Narratives - new page
    doc.addPage(); y = margin;
    doc.setFillColor(26, 43, 74); doc.rect(0, 0, W, 14, 'F');
    sf('bold', 11); doc.setTextColor(255, 255, 255);
    doc.text('Daily Narrative Notes (Required)', margin, 10);
    doc.setTextColor(40, 40, 40); y = 22;

    DAYS.forEach(day => {
      const text = form.narratives[day] || '';
      const lines = doc.splitTextToSize(text || '(No notes entered)', W - margin * 2 - 28);
      const bh = Math.max(14, lines.length * 5 + 6);
      doc.setFillColor(247, 244, 239); doc.roundedRect(margin, y, W - margin * 2, bh, 2, 2, 'F');
      doc.setDrawColor(210, 210, 210); doc.roundedRect(margin, y, W - margin * 2, bh, 2, 2);
      sf('bold', 9); doc.setTextColor(26, 43, 74); doc.text(day + ':', margin + 3, y + 7);
      sf('normal', 9); doc.setTextColor(50, 50, 50); doc.text(lines, margin + 18, y + 7);
      y += bh + 4;
    });

    // Signatures — ensure enough room on page (need ~85mm with attestation)
    if (y + 85 > 265) { doc.addPage(); y = margin; }
    y += 6;
    doc.setFillColor(26, 43, 74); doc.rect(margin, y, W - margin * 2, 7, 'F');
    sf('bold', 9); doc.setTextColor(255, 255, 255); doc.text('Signatures', margin + 3, y + 5);
    doc.setTextColor(40, 40, 40); y += 12;

    // Attestation statement
    sf('normal', 7.5); doc.setTextColor(60, 60, 75);
    const attLines = doc.splitTextToSize(ATTESTATION, W - margin * 2 - 8);
    const attH = attLines.length * 4.2 + 8;
    doc.setFillColor(247, 244, 239);
    doc.roundedRect(margin, y, W - margin * 2, attH, 2, 2, 'F');
    doc.text(attLines, margin + 4, y + 5);
    doc.setTextColor(40, 40, 40); y += attH + 7;
    const sigW = (W - margin * 2) / 2 - 6;
    const rx = margin + sigW + 12;

    // Labels — both at same y before incrementing
    sf('bold', 9);
    doc.text('Caregiver Signature:', margin, y);
    doc.text('Client Signature:', rx, y);
    y += 4;

    // Signature boxes — both start at same y
    doc.setDrawColor(180, 180, 180);
    doc.rect(margin, y, sigW, 22);
    const cgImg = getCanvasData(cgCanvasRef);
    if (cgImg && cgImg.length > 200) doc.addImage(cgImg, 'PNG', margin + 1, y + 1, sigW - 2, 20);
    doc.rect(rx, y, sigW, 22);
    const clientImg = getCanvasData(clientCanvasRef);
    if (clientImg && clientImg.length > 200) doc.addImage(clientImg, 'PNG', rx + 1, y + 1, sigW - 2, 20);

    // Date lines — both at same y, clearly below boxes
    y += 26;
    sf('normal', 9);
    const fmtSigDate = d => d ? d.replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$2/$3/$1') : '___________';
    doc.text('Date: ' + fmtSigDate(form.sigs.cgDate), margin, y);
    doc.text('Date: ' + fmtSigDate(form.sigs.clientDate), rx, y);

    const filename = `HCA_Notes_${(form.clientName || form.caregiver || 'form').replace(/\s+/g, '_')}_${form.weekBegin || 'nodate'}.pdf`;
    doc.save(filename);
    showToast('PDF downloaded!', 'success');
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), type === 'error' ? 5000 : 3000);
  };

  if (loading) return (
    <div className="loading-screen"><div className="spinner" /></div>
  );

  return (
    <div className="app-shell-with-sidebar">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} />

      <main className="main-content">
        <div className="top-bar">
          <div>
            <div className="page-title">{isEditing ? 'Edit Progress Note' : 'New Progress Note'}</div>
            <div className="page-subtitle">Weekly caregiver documentation</div>
          </div>
          <div className="top-bar-right">
            {isDirty && <span style={{ fontSize: 12, color: 'var(--gold)', fontWeight: 600 }}>● Unsaved</span>}
            <button className="btn btn-ghost btn-sm" onClick={() => safeNavigate('/dashboard')}>← Dashboard</button>
            {user?.isAdmin && <button className="btn btn-ghost btn-sm" onClick={downloadPDF}>⬇ PDF</button>}
            <button className="btn btn-teal btn-sm" onClick={collectAndSave} disabled={saving || isLocked}>
              {saving ? 'Saving…' : isLocked ? '🔒 Locked' : '☁ Save'}
            </button>
          </div>
        </div>

        {isLocked && (
          <div style={{
            background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8,
            padding: '12px 18px', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 10,
            color: '#856404', fontWeight: 500, fontSize: 14,
          }}>
            <span style={{ fontSize: 18 }}>🔒</span>
            This progress note has been signed by both the caregiver and client and is now locked.
            Contact an administrator to request changes.
          </div>
        )}

        {/* CLIENT INFO */}
        <div className="card">
          <div className="card-header"><span>👤</span> Client Information</div>
          <div className="card-body">
            {availableClients.length > 0 && (
              <div className="form-group-card" style={{ marginBottom: 16 }}>
                <label className="form-label">Select Client</label>
                <select className="form-input" style={{ maxWidth: 320 }}
                  value={selectedClientId}
                  disabled={isLocked}
                  onChange={e => {
                    const cid = e.target.value;
                    setSelectedClientId(cid);
                    if (cid) {
                      const c = availableClients.find(x => x.clientId === cid);
                      if (c) {
                        set('clientName', c.clientName);
                        set('dob', c.dob || '');
                        set('medicaidId', c.medicaidId || '');
                      }
                    }
                  }}>
                  <option value="">— Select a client —</option>
                  {availableClients.map(c => (
                    <option key={c.clientId} value={c.clientId}>{c.clientName}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14 }}>
              {[['clientName', 'Client Name', 'text', 'Full name', true],
                ['dob', 'Date of Birth', 'date', '', true],
                ['medicaidId', 'Medicaid ID', 'text', 'ID number', true],
                ['weekBegin', 'Week Beginning', 'date', '', true]].map(([field, label, type, ph, required]) => (
                <div className="form-group-card" key={field}>
                  <label className="form-label">
                    {label} {required && <span style={{ color: 'var(--danger)' }}>*</span>}
                  </label>
                  <input className="form-input" type={type} value={form[field] || ''} placeholder={ph}
                    onChange={e => set(field, e.target.value)}
                    disabled={isLocked}
                    style={{ borderColor: required && !form[field] && isDirty ? 'var(--danger)' : undefined }} />
                </div>
              ))}
            </div>
            <div style={{ marginTop: 14 }}>
              <label className="form-label">
                Caregiver Name <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input className="form-input" type="text" value={form.caregiver || ''}
                onChange={e => set('caregiver', e.target.value)} disabled={isLocked} style={{ maxWidth: 320,
                  borderColor: !form.caregiver && isDirty ? 'var(--danger)' : undefined }} />
            </div>
            <p style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>
              <span style={{ color: 'var(--danger)' }}>*</span> Required fields
            </p>
          </div>
        </div>

        {/* TIME TRACKING */}
        <div className="card">
          <div className="card-header"><span>⏰</span> Time Tracking</div>
          <div className="card-body" style={{ padding: 0 }}>
            <div className="day-grid-scroll-wrap"><div className="day-grid">
              <div className="day-grid-header">Field</div>
              {DAYS.map(d => <div key={d} className="day-grid-header">{d}</div>)}

              <div className="day-grid-label">Time In</div>
              {DAYS.map(day => (
                <div key={day} className="day-grid-cell">
                  <input type="time" value={form.timeIn[day] || ''}
                    onChange={e => setTime('timeIn', day, e.target.value)} disabled={isLocked} />
                </div>
              ))}

              <div className="day-grid-label">Time Out</div>
              {DAYS.map(day => (
                <div key={day} className="day-grid-cell">
                  <input type="time" value={form.timeOut[day] || ''}
                    onChange={e => setTime('timeOut', day, e.target.value)} disabled={isLocked} />
                </div>
              ))}

              <div className="day-grid-label" style={{ fontWeight: 700, color: 'var(--navy)' }}>Total Hours</div>
              {DAYS.map(day => (
                <div key={day} className="day-grid-cell"
                  style={{ fontWeight: 700, color: 'var(--teal)', fontSize: 13 }}>
                  {calcTotal(day)}
                </div>
              ))}
            </div></div>
          </div>
        </div>

        {/* ADLs */}
        <div className="card">
          <div className="card-header"><span>🧼</span> Activities of Daily Living (ADLs)</div>
          <div className="card-body" style={{ padding: 0 }}>
            <div className="day-grid-scroll-wrap"><div className="day-grid">
              <div className="day-grid-header">Activity</div>
              {DAYS.map(d => <div key={d} className="day-grid-header">{d}</div>)}
              {ADLS.map(adl => (
                <React.Fragment key={adl}>
                  <div className="day-grid-label">{adl}</div>
                  {DAYS.map(day => (
                    <div key={day} className="check-cell">
                      <input type="checkbox"
                        checked={(form.adls[adl] || {})[day] || false}
                        onChange={e => setCheck('adls', adl, day, e.target.checked)}
                        disabled={isLocked} />
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div></div>
          </div>
        </div>

        {/* IADLs */}
        <div className="card">
          <div className="card-header"><span>🏠</span> Instrumental Activities of Daily Living (IADLs)</div>
          <div className="card-body" style={{ padding: 0 }}>
            <div className="day-grid-scroll-wrap"><div className="day-grid">
              <div className="day-grid-header">Activity</div>
              {DAYS.map(d => <div key={d} className="day-grid-header">{d}</div>)}
              {IADLS.map(iadl => (
                <React.Fragment key={iadl}>
                  <div className="day-grid-label">{iadl}</div>
                  {DAYS.map(day => (
                    <div key={day} className="check-cell">
                      <input type="checkbox"
                        checked={(form.iadls[iadl] || {})[day] || false}
                        onChange={e => setCheck('iadls', iadl, day, e.target.checked)}
                        disabled={isLocked} />
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div></div>
          </div>
        </div>

        {/* NARRATIVES */}
        <div className="card">
          <div className="card-header"><span>📝</span> Daily Narrative Notes (Required)</div>
          <div className="card-body">
            <div className="narrative-grid">
              {DAYS.map(day => (
                <div key={day} className="narrative-row">
                  <div className="narrative-day-label">{day}:</div>
                  <textarea value={form.narratives[day] || ''}
                    onChange={e => setNarrative(day, e.target.value)}
                    placeholder={`Enter notes for ${day}…`}
                    disabled={isLocked} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SIGNATURES */}
        <div className="card">
          <div className="card-header"><span>✍️</span> Signatures</div>
          <div className="card-body">
            <p style={{
              fontSize: 12, color: '#4a5568', lineHeight: 1.75,
              marginBottom: 22, padding: '13px 16px',
              background: '#f7f4ef', borderRadius: 6,
              borderLeft: '3px solid var(--teal)',
            }}>
              {ATTESTATION}
            </p>
            <div className="sig-grid">
              <div className="sig-block">
                <div className="sig-label">Caregiver Signature</div>
                <div className="sig-pad-wrap">
                  <canvas ref={cgCanvasRef} className="sig-canvas" width={600} height={165} />
                  <button className="sig-clear-btn" onClick={() => clearSig(cgCanvasRef, cgSignedRef)} disabled={isLocked}>✕ Clear</button>
                </div>
                <div style={{ marginTop: 10 }}>
                  <label className="form-label">Date</label>
                  <input className="form-input" type="date" value={form.sigs.cgDate || ''}
                    onChange={e => { setIsDirty(true); setForm(f => ({ ...f, sigs: { ...f.sigs, cgDate: e.target.value } })); }}
                    disabled={isLocked} />
                </div>
              </div>
              <div className="sig-block">
                <div className="sig-label">Client Signature</div>
                <div className="sig-pad-wrap">
                  <canvas ref={clientCanvasRef} className="sig-canvas" width={600} height={165} />
                  <button className="sig-clear-btn" onClick={() => clearSig(clientCanvasRef, clientSignedRef)} disabled={isLocked}>✕ Clear</button>
                </div>
                <div style={{ marginTop: 10 }}>
                  <label className="form-label">Date</label>
                  <input className="form-input" type="date" value={form.sigs.clientDate || ''}
                    onChange={e => { setIsDirty(true); setForm(f => ({ ...f, sigs: { ...f.sigs, clientDate: e.target.value } })); }}
                    disabled={isLocked} />
                </div>
              </div>
            </div>
          </div>
          <div className="action-bar">
            <button className="btn btn-ghost" onClick={() => safeNavigate('/dashboard')}>← Back</button>
            {user?.isAdmin && <button className="btn btn-ghost" onClick={downloadPDF}>⬇ Download PDF</button>}
            <button className="btn btn-teal" onClick={collectAndSave} disabled={saving || isLocked}>
              {saving ? 'Saving…' : isLocked ? '🔒 Locked' : '☁ Save to Cloud'}
            </button>
          </div>
        </div>
      </main>

      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 16,
        }}>
          <div style={{
            background: 'var(--warm-white)', borderRadius: 14,
            padding: 32, maxWidth: 420, width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontSize: 36, marginBottom: 12, textAlign: 'center' }}>⚠️</div>
            <h3 style={{
              fontFamily: 'DM Serif Display, serif', fontSize: 20,
              color: 'var(--navy)', marginBottom: 10, textAlign: 'center',
            }}>Unsaved Changes</h3>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 24, textAlign: 'center', lineHeight: 1.6 }}>
              You have unsaved changes to this progress note.<br />
              Would you like to save before leaving?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button className="btn btn-teal" onClick={handleSaveAndNavigate} disabled={saving}
                style={{ justifyContent: 'center' }}>
                {saving ? 'Saving…' : '💾 Save & Continue'}
              </button>
              <button className="btn btn-ghost" onClick={handleDiscardAndNavigate}
                style={{ justifyContent: 'center', color: 'var(--danger)', borderColor: 'var(--danger)' }}>
                🗑 Discard Changes & Leave
              </button>
              <button className="btn btn-ghost" onClick={handleCancelNavigation}
                style={{ justifyContent: 'center' }}>
                ← Stay on This Page
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`toast ${toast.type}`}>{toast.type === 'success' ? '✅' : toast.type === 'info' ? 'ℹ️' : '❌'} {toast.msg}</div>}
    </div>
  );
}
